import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect } from "react";
import { Bold, Italic, List, ListOrdered, Quote, Heading2, Undo2, Redo2 } from "lucide-react";

function ToolbarButton({ onClick, active, disabled, testId, children }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      className={`p-2 rounded-md text-[#1C201E] hover:bg-[#F3EFEA] transition-colors ${active ? "bg-[#2C4C3B] text-white hover:bg-[#1A3326]" : ""} disabled:opacity-40`}
    >
      {children}
    </button>
  );
}

export default function RichText({ value, onChange, testId = "rich-editor", placeholder }) {
  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: { levels: [2, 3] } })],
    content: value || "",
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none min-h-[160px] p-4 focus:outline-none",
        "data-testid": `${testId}-content`,
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div className="border border-[#E5E0D8] rounded-lg bg-white overflow-hidden">
      <div className="flex flex-wrap items-center gap-1 px-2 py-1 border-b border-[#E5E0D8] bg-[#FAF9F6]">
        <ToolbarButton testId={`${testId}-bold`} onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")}><Bold className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton testId={`${testId}-italic`} onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")}><Italic className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton testId={`${testId}-h2`} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })}><Heading2 className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton testId={`${testId}-bullet`} onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")}><List className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton testId={`${testId}-ordered`} onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")}><ListOrdered className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton testId={`${testId}-quote`} onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")}><Quote className="w-4 h-4" /></ToolbarButton>
        <div className="mx-1 h-5 w-px bg-[#E5E0D8]" />
        <ToolbarButton testId={`${testId}-undo`} onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}><Undo2 className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton testId={`${testId}-redo`} onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}><Redo2 className="w-4 h-4" /></ToolbarButton>
      </div>
      <EditorContent editor={editor} data-testid={testId} />
    </div>
  );
}
